import { useEffect, type RefObject } from 'react';

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])', 'select:not([disabled])',
  'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])', '[contenteditable="true"]',
].join(',');

export function focusableWithin(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((element) => {
    if (element.closest('[hidden]') || element.getAttribute('aria-hidden') === 'true') return false;
    return element.getClientRects().length > 0;
  });
}

/**
 * Keeps Tab inside `ref` while `active`, moves focus in on activation, and restores it to the
 * element that was focused before (01-foundations §7.2). `initialFocus` picks the landing spot:
 * the dialog's title by default, so a screen reader announces it, or a named control.
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean, initialFocus?: () => HTMLElement | null): void {
  useEffect(() => {
    if (!active) return;
    const root = ref.current;
    if (!root) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Why after paint: an element still transitioning in cannot take focus in some engines. Why the landing spot is
    // picked inside the frame and not when the effect runs: a body that fills between the two (a form whose list
    // answered from cache) would otherwise send focus to the title though its first control already stands.
    const frame = requestAnimationFrame(() => (initialFocus?.() ?? root).focus({ preventScroll: true }));

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab' || event.isComposing) return;
      const items = focusableWithin(root);
      if (items.length === 0) {
        event.preventDefault();
        root.focus();
        return;
      }
      const first = items[0] as HTMLElement;
      const last = items[items.length - 1] as HTMLElement;
      const current = document.activeElement;
      if (event.shiftKey && (current === first || current === root || !root.contains(current))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (current === last || !root.contains(current))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKeyDown, true);
      // Why: deferred so the inert flag on the page (useInertBackground) is lifted before focus
      // returns to an element inside it; an inert element silently refuses focus.
      if (previous) {
        requestAnimationFrame(() => {
          if (previous.isConnected) previous.focus({ preventScroll: true });
        });
      }
    };
  }, [ref, active, initialFocus]);
}

/** Marks everything outside the overlay inert while it is open (03-components §4.1). */
export function useInertBackground(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const root = document.getElementById('root');
    if (!root) return;
    const wasInert = root.inert;
    root.inert = true;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      root.inert = wasInert;
      document.body.style.overflow = previousOverflow;
    };
  }, [active]);
}
